import ApiService from "@/core/services/ApiService";
import { AssetsMutations, AssetsActions } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";

@Module
export default class AssetsModule extends VuexModule {
  errorCode = 0;
  timeout = null;
  errors: Array<string> = [];
  statesFilter = [];
  usersFilter = [];
  usersVisitsFilter = [];
  usersVisitsWithDonorsFilter = [];
  terminationReasonsFilter = [];
  transportsFilter = [];
  collectorsFilter = [];
  rolesFilter = [];
  claimReasonsFilter = [];
  rejectionReasonsRequestFilter = [];
  terminationReasons = [];
  isloading = false;
  pendingRequests = false;

  get getUsersFilter(): Array<string> {
    return this.usersFilter;
  }

  get getClaimReasonsFilter(): Array<string> {
    return this.claimReasonsFilter;
  }

  get getRejectionReasonsRequestFilter(): Array<string> {
    return this.rejectionReasonsRequestFilter;
  }

  get getUsersVisitsFilter(): Array<string> {
    return this.usersVisitsFilter;
  }

  get getUsersVisitsWithDonorsFilter(): Array<string> {
    return this.usersVisitsWithDonorsFilter;
  }

  get getTerminationReasonsFilter(): Array<string> {
    return this.terminationReasonsFilter;
  }

  get getTransportsFilter(): Array<string> {
    return this.transportsFilter;
  }

  get getCollectorsFilter(): Array<string> {
    return this.collectorsFilter;
  }

  get getRolesFilter(): Array<string> {
    return this.rolesFilter;
  }

  get getStatesFilter(): Array<string> {
    return this.statesFilter;
  }

  get getAssetsErrors(): Array<string> {
    return this.errors;
  }

  get getAssetsErrorCode(): number {
    return this.errorCode;
  }

  get isLoading(): boolean {
    return this.isloading;
  }

  get hasPendingRequests(): boolean {
    return this.pendingRequests;
  }

  @Mutation
  [AssetsMutations.SET_ERROR](error) {
    this.errors = error.errors;
    this.errorCode = error.errorCode;
  }

  @Mutation
  [AssetsMutations.SET_USERS](users) {
    this.usersFilter = users;
  }

  @Mutation
  [AssetsMutations.SET_USERS_VISITS](users) {
    this.usersVisitsFilter = users;
  }

  @Mutation
  [AssetsMutations.SET_USERS_VISITS_WITH_ASSOCIATED_DONORS](users) {
    this.usersVisitsWithDonorsFilter = users;
  }

  @Mutation
  [AssetsMutations.SET_TERMINATION_REASONS](reasons) {
    this.terminationReasonsFilter = reasons;
  }

  @Mutation
  [AssetsMutations.SET_CLAIM_REASONS](reasons) {
    this.claimReasonsFilter = reasons;
  }

  @Mutation
  [AssetsMutations.SET_REJECTION_REASONS_REQUEST](reasons) {
    this.rejectionReasonsRequestFilter = reasons;
  }

  @Mutation
  [AssetsMutations.SET_TRANSPORTS](transports) {
    this.transportsFilter = transports;
  }

  @Mutation
  [AssetsMutations.SET_COLLECTORS](collectors) {
    this.collectorsFilter = collectors;
  }

  @Mutation
  [AssetsMutations.SET_ROLES](roles) {
    this.rolesFilter = roles;
  }

  @Mutation
  [AssetsMutations.SET_STATES](states) {
    this.statesFilter = states;
  }

  @Mutation
  [AssetsMutations.LOADING](loading) {
    this.isloading = loading;
  }

  @Mutation
  [AssetsMutations.SET_PENDING_REQUEST]() {
    this.pendingRequests = true;
  }

  @Mutation
  [AssetsMutations.CLEAR_PENDING_REQUEST]() {
    this.pendingRequests = false;
  }

  @Action
  [AssetsActions.GET_USERS]() {
    ApiService.setHeader();
    return new Promise<void>((resolve, reject) => {
      ApiService.get("index", "users")
        .then(({ data }) => {
          this.context.commit(AssetsMutations.SET_USERS, data);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(AssetsMutations.SET_ERROR, {
            errors: response?.data.errors,
            errorCode: response?.status,
          });
          reject();
        });
    });
  }

  @Action
  [AssetsActions.GET_USERS_VISITS]() {
    ApiService.setHeader();
    return new Promise<void>((resolve, reject) => {
      ApiService.query("index/users", {
        params: { permission: ["place_visit"] },
      })
        .then(({ data }) => {
          this.context.commit(AssetsMutations.SET_USERS_VISITS, data);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(AssetsMutations.SET_ERROR, {
            errors: response?.data.errors,
            errorCode: response?.status,
          });
          reject();
        });
    });
  }

  @Action
  [AssetsActions.GET_USERS_VISITS_WITH_ASSOCIATED_DONORS]() {
    ApiService.setHeader();
    return new Promise<void>((resolve, reject) => {
      ApiService.query("index/users", {
        params: {
          permission: ["place_visit"],
          has_donors: 1,
        },
      })
        .then(({ data }) => {
          this.context.commit(
            AssetsMutations.SET_USERS_VISITS_WITH_ASSOCIATED_DONORS,
            data
          );
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(AssetsMutations.SET_ERROR, {
            errors: response?.data.errors,
            errorCode: response?.status,
          });
          reject();
        });
    });
  }

  @Action
  [AssetsActions.GET_STATES]() {
    ApiService.setHeader();
    return new Promise<void>((resolve, reject) => {
      ApiService.get("index", "states")
        .then(({ data }) => {
          this.context.commit(AssetsMutations.SET_STATES, data);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(AssetsMutations.SET_ERROR, {
            errors: response?.data.errors,
            errorCode: response?.status,
          });
          reject();
        });
    });
  }

  @Action
  [AssetsActions.GET_TERMINATION_REASONS]() {
    ApiService.setHeader();
    return new Promise<void>((resolve, reject) => {
      ApiService.get("index", "termination_reasons")
        .then(({ data }) => {
          this.context.commit(AssetsMutations.SET_TERMINATION_REASONS, data);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(AssetsMutations.SET_ERROR, {
            errors: response?.data.errors,
            errorCode: response?.status,
          });
          reject();
        });
    });
  }

  @Action
  [AssetsActions.GET_TRANSPORTS]() {
    ApiService.setHeader();
    return new Promise<void>((resolve, reject) => {
      ApiService.get("index", "transports")
        .then(({ data }) => {
          this.context.commit(AssetsMutations.SET_TRANSPORTS, data);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(AssetsMutations.SET_ERROR, {
            errors: response?.data.errors,
            errorCode: response?.status,
          });
          reject();
        });
    });
  }

  @Action
  [AssetsActions.GET_COLLECTORS]() {
    ApiService.setHeader();
    return new Promise<void>((resolve, reject) => {
      ApiService.get("index", "collectors")
        .then(({ data }) => {
          this.context.commit(AssetsMutations.SET_COLLECTORS, data);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(AssetsMutations.SET_ERROR, {
            errors: response?.data.errors,
            errorCode: response?.status,
          });
          reject();
        });
    });
  }

  @Action
  [AssetsActions.GET_ROLES]() {
    ApiService.setHeader();
    return new Promise<void>((resolve, reject) => {
      ApiService.get("index", "roles")
        .then(({ data }) => {
          this.context.commit(AssetsMutations.SET_ROLES, data);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(AssetsMutations.SET_ERROR, {
            errors: response?.data.errors,
            errorCode: response?.status,
          });
          reject();
        });
    });
  }

  @Action
  [AssetsActions.GET_CLAIM_REASONS]() {
    ApiService.setHeader();
    return new Promise<void>((resolve, reject) => {
      ApiService.get("claims/reasons")
        .then(({ data }) => {
          this.context.commit(AssetsMutations.SET_CLAIM_REASONS, data);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(AssetsMutations.SET_ERROR, {
            errors: response?.data.errors,
            errorCode: response?.status,
          });
          reject();
        });
    });
  }

  @Action
  [AssetsActions.GET_REJECTION_REASONS_REQUEST]() {
    ApiService.setHeader();
    return new Promise<void>((resolve, reject) => {
      ApiService.get("rejection_reasons_request")
        .then(({ data }) => {
          data.data.sort((a, b) => a.id - b.id);
          this.context.commit(
            AssetsMutations.SET_REJECTION_REASONS_REQUEST,
            Array.from(data.data)
          );
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(AssetsMutations.SET_ERROR, {
            errors: response?.data.errors,
            errorCode: response?.status,
          });
          reject();
        });
    });
  }

  @Action
  [AssetsActions.SET_LOADING](loading) {
    this.context.commit(AssetsMutations.LOADING, loading);
  }
}
